import type { User } from '@supabase/supabase-js';

export type LastUsedLoginType = 'google' | 'azure' | 'email';

const STORAGE_KEY = 'upstand:last_used_login_type';

export function getLastUsedLoginType(): LastUsedLoginType | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'google' || v === 'azure' || v === 'email') return v;
    return null;
  } catch {
    return null;
  }
}

export function setLastUsedLoginType(type: LastUsedLoginType): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, type);
  } catch {
    // private mode / quota - ignore
  }
}

export function lastUsedLoginLabel(type: LastUsedLoginType): string {
  switch (type) {
    case 'google':
      return 'Google';
    case 'azure':
      return 'Microsoft';
    case 'email':
      return 'Email & password';
    default:
      return '';
  }
}

/** Map Supabase auth provider id to stored login surface. */
export function providerToLastUsedLoginType(provider: string | undefined): LastUsedLoginType | null {
  if (!provider) return null;
  const p = provider.toLowerCase();
  if (p === 'google') return 'google';
  // Azure AD / Entra and legacy naming
  if (p === 'azure' || p === 'microsoft') return 'azure';
  if (p === 'email') return 'email';
  return null;
}

/** Resolve last-used login UI bucket from Supabase user (covers PKCE OAuth + identities). */
export function lastUsedLoginTypeFromUser(user: User | null | undefined): LastUsedLoginType | null {
  if (!user) return null;

  const meta = user.app_metadata as Record<string, unknown> | undefined;
  if (meta) {
    const single = typeof meta.provider === 'string' ? meta.provider : undefined;
    const fromSingle = providerToLastUsedLoginType(single);
    if (fromSingle) return fromSingle;

    const provs = meta.providers;
    if (Array.isArray(provs)) {
      for (const x of provs) {
        if (typeof x === 'string') {
          const t = providerToLastUsedLoginType(x);
          if (t) return t;
        }
      }
    }
  }

  for (const id of user.identities ?? []) {
    const t = providerToLastUsedLoginType(id?.provider);
    if (t) return t;
  }

  return null;
}
